/**
 * Script: Bonsai search hooks (bonsai-search)
 * -----------------------------------------------------------------------------
 * Global hooks for the <bonsai-search> web component, loaded site-wide when
 * Bonsai search is enabled. The component resolves these from `window` via its
 * `on-price` and `on-filter-panel-render` attributes; the title cleanup runs
 * off the component's composed `results` events.
 *
 */
(function () {
  if (window.bonsaiSearchPrice) {
    return
  }

  var RESULTS_WINDOW = 50
  var followUpStates = new WeakMap()

  /**
   * Shopper-facing copy, set by the search drawer from the theme's locales.
   * - Falls back to English so the component still reads correctly if the
   *   asset runs before the drawer has mounted.
   */
  var FALLBACK_LABELS = {
    apply: 'Apply',
    clear: 'Clear',
    close: 'Close',
    filter: 'Filter by',
    filterTitle: 'Filter',
    priceRanges: {
      over: '{{ price }}+',
      range: '{{ min }} \u2013 {{ max }}',
      under: 'Under {{ price }}',
    },
    quickView: 'Quick View',
    scrollNext: 'More questions',
    scrollPrevious: 'Previous questions',
    sort: 'Sort by',
    sortOptions: {
      price_asc: 'Price, low to high',
      price_desc: 'Price, high to low',
      relevance: 'Relevance',
    },
    sortTitle: 'Sort',
  }

  /**
   * Read one label, falling back to English.
   * @param {String} name - Label name.
   * @returns {String}
   */
  function label(name) {
    var labels = window.bonsaiSearchLabels || {}
    return labels[name] || FALLBACK_LABELS[name]
  }

  /**
   * Read one sort option's label, falling back to English.
   * @param {String} value - Sort option value.
   * @returns {String}
   */
  function sortLabel(value) {
    var labels = (window.bonsaiSearchLabels || {}).sortOptions || {}
    return labels[value] || FALLBACK_LABELS.sortOptions[value]
  }

  /**
   * The price filter's field, and the fixed ranges offered for it in whole
   * dollars.
   * - The API filters price in cents, so each range ends a cent below the
   *   start of the next, keeping a product in a single range.
   */
  var PRICE_FIELD = 'price_cents'
  var PRICE_RANGES = [
    { max: 500 },
    { max: 1000, min: 500 },
    { max: 2500, min: 1000 },
    { max: 5000, min: 2500 },
    { min: 5000 },
  ]

  var observedContainers = new WeakSet()
  var paintedSearches = new WeakSet()
  var observedDropdowns = new WeakSet()
  var containerBadges = new WeakMap()
  var containerWindows = new WeakMap()
  var windowObservers = new WeakMap()
  var filterRoots = []
  var pendingTriggerFocus = null

  /**
   * Format a result price to match the theme, e.g. $1,995 / CAD $2,617.
   * - Formatted by the drawer where it has mounted, as only it can reach the
   *   store's money format and the market's currency.
   * @param {Object} result - Bonsai search result.
   * @param {String} price - Raw price string, e.g. "1995.00".
   * @returns {String}
   */
  window.bonsaiSearchPrice = function (result, price) {
    var formatted = window.bonsaiSearchFormatPrice && window.bonsaiSearchFormatPrice(price)

    if (formatted) {
      return formatted
    }

    var number = Number(price)

    if (!Number.isFinite(number)) {
      return price || ''
    }

    var hasCents = Math.round(number * 100) % 100 !== 0

    return '$' + number.toLocaleString('en-US', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    })
  }

  /**
   * Shorten result titles to match product cards by stripping the "|SKU"
   * suffix, e.g. "Naga Wrap, Sterling Silver, 2.5MM|BUS603421BSP".
   * @param {Element} container - The component's results container.
   */
  function cleanTitles(container) {
    container.querySelectorAll('[class*="bonsai-result-title"]').forEach(function (title) {
      var text = title.textContent || ''
      var index = text.indexOf('|')

      if (index > -1) {
        title.textContent = text.slice(0, index).trim()
      }
    })
  }

  /**
   * Enhance result cards to match product cards: shortened titles, same-tab
   * navigation, merchandising badges, a quick view button opening the
   * theme's quick add drawer, and the Wishlist King heart.
   * @param {Element} container - The component's results container.
   */
  function enhanceCards(container) {
    cleanTitles(container)
    ensureCardStyles(container.getRootNode())

    var badges = containerBadges.get(container) || {}

    container.querySelectorAll('[class*="bonsai-result-card"]').forEach(function (card) {
      if (card.tagName === 'A') {
        card.target = '_self'
      }

      var wrapper = card.querySelector('[class*="bonsai-result-image-wrapper"]')

      if (!wrapper) {
        return
      }

      var badge = badges[getCardPath(card)]

      if (badge && !wrapper.querySelector('.jh-badge')) {
        wrapper.appendChild(buildBadge(badge))
      }

      if (!wrapper.querySelector('.jh-quick-view')) {
        wrapper.appendChild(buildQuickView(card))
      }

      if (window.WishlistKing && !wrapper.querySelector('wishlist-button-collection')) {
        var heart = buildWishlistButton(card)

        if (heart) {
          wrapper.appendChild(heart)
        }
      }
    })

    applyResultsWindow(container)
    enhanceFollowUps(container)
  }

  /**
   * Window the rendered cards so only the first batch shows, growing by a
   * batch each time the shopper scrolls near the last visible card.
   * @param {Element} container - The component's results container.
   */
  function applyResultsWindow(container) {
    var cards = container.querySelectorAll('[class*="bonsai-result-card"]')
    var count = containerWindows.get(container) || RESULTS_WINDOW
    var lastVisible = null

    cards.forEach(function (card, index) {
      card.classList.toggle('jh-card-hidden', index >= count)

      if (index < count) {
        lastVisible = card
      }
    })

    observeLastCard(container, cards.length > count ? lastVisible : null)
  }

  /**
   * Reveal the next batch of cards when the last visible card comes within
   * a viewport and a half of view.
   * @param {Element} container - The component's results container.
   * @param {Element} [card] - Last visible card, or null when all cards show.
   */
  function observeLastCard(container, card) {
    var observer = windowObservers.get(container)

    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        var isNear = entries.some(function (entry) {
          return entry.isIntersecting
        })

        if (!isNear) {
          return
        }

        var count = containerWindows.get(container) || RESULTS_WINDOW
        containerWindows.set(container, count + RESULTS_WINDOW)
        applyResultsWindow(container)
      }, { rootMargin: '150% 0px' })

      windowObservers.set(container, observer)
    }

    observer.disconnect()

    if (card) {
      observer.observe(card)
    }
  }

  /**
   * Map result URL paths to their merchandising badge, read from the same
   * `badge:` product tags as the product-card component.
   * @param {Object[]} results - Bonsai search results.
   * @returns {Object}
   */
  function buildBadgeMap(results) {
    var badges = {}

    results.forEach(function (result) {
      var badge = (result.tags || []).filter(function (tag) {
        return tag.includes('badge:')
      })[0]

      if (!badge || !result.publicUrl) {
        return
      }

      var path = getCardPath({ href: result.publicUrl })

      if (path) {
        badges[path] = badge.replace('badge:', '').trim()
      }
    })

    return badges
  }

  /**
   * Build the merchandising badge for one result card.
   * @param {String} label - Badge label from the product's `badge:` tag.
   * @returns {Element}
   */
  function buildBadge(label) {
    var badge = document.createElement('span')
    badge.className = 'jh-badge'
    badge.textContent = label
    return badge
  }

  /**
   * Pathname of a result card's product URL.
   * @param {Object} card - Result card anchor, or any object with an href.
   * @returns {String}
   */
  function getCardPath(card) {
    try {
      return new URL(card.href, window.location.origin).pathname
    } catch (error) {
      return ''
    }
  }

  /**
   * Build the quick view trigger for one result card.
   * @param {Element} card - Result card anchor.
   * @returns {Element}
   */
  function buildQuickView(card) {
    var trigger = label('quickView')

    var button = document.createElement('button')
    button.type = 'button'
    button.className = 'jh-quick-view'
    button.setAttribute('aria-label', trigger)

    var text = document.createElement('span')
    text.className = 'jh-quick-view__label'
    text.textContent = trigger

    var plus = document.createElement('span')
    plus.className = 'jh-quick-view__plus'
    plus.textContent = '+'

    button.appendChild(text)
    button.appendChild(plus)

    button.addEventListener('click', function (event) {
      event.preventDefault()
      event.stopPropagation()

      var path = getCardPath(card)

      if (!path) {
        return
      }

      var segments = path.split('/').filter(Boolean)
      button.dispatchEvent(new CustomEvent('bonsai:quick-view', {
        bubbles: true,
        composed: true,
        detail: {
          handle: segments[segments.length - 1] || '',
          trigger: button,
          url: card.href,
        },
      }))
    })

    return button
  }

  /**
   * Build the Wishlist King heart for one result card.
   * - The app injects its own buttons by watching the document, which cannot
   *   see into the closed shadow root, so the element is created here and
   *   the app's custom element upgrades it where it lands.
   * - The click's default is prevented as the card is an anchor, which would
   *   otherwise navigate as the heart is toggled.
   * @param {Element} card - Result card anchor.
   * @returns {Element|null}
   */
  function buildWishlistButton(card) {
    var path = getCardPath(card)
    var handle = path && path.split('/').filter(Boolean).pop()

    if (!handle) {
      return null
    }

    var heart = document.createElement('wishlist-button-collection')
    heart.setAttribute('data-product-handle', handle)
    heart.setAttribute('show-icon', '')
    heart.setAttribute('icon', 'wishlist')

    heart.addEventListener('click', function (event) {
      event.preventDefault()
    })

    return heart
  }

  /**
   * Rebuild the wishlist hearts when the page returns from the back-forward
   * cache, as the restored page keeps the hearts frozen at their old state
   * while the wishlist may have changed on the page navigated to; the app
   * refreshes its own buttons but cannot see into the closed shadow root.
   * Freshly created elements upgrade against the current wishlist.
   */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted || !window.WishlistKing) {
      return
    }

    filterRoots.forEach(function (root) {
      root.querySelectorAll('wishlist-button-collection').forEach(function (heart) {
        heart.remove()
      })

      root.querySelectorAll('[class*="bonsai-result-card"]').forEach(function (card) {
        var wrapper = card.querySelector('[class*="bonsai-result-image-wrapper"]')
        var heart = wrapper && buildWishlistButton(card)

        if (heart) {
          wrapper.appendChild(heart)
        }
      })
    })
  })

  /**
   * Card styles, injected once per shadow root.
   * - Cards render visible immediately as the SDK's staggered fade-in would
   *   leave late batches of a windowed result set invisible for seconds.
   * - Hidden cards sit beyond the current scroll window.
   * - Badge: merchandising badge matching the product-card badge. Its colour is
   *   set explicitly as the card is an anchor and the badge would otherwise
   *   inherit the browser's link colour inside the shadow root.
   * - Quick view desktop: theme quick add bar shown on card hover.
   * - Quick view mobile: theme quick add plus button, always visible.
   * @param {Node} root - The component's shadow root.
   */
  function ensureCardStyles(root) {
    if (!root || !root.host || root.bonsaiJhCardStyles) {
      return
    }

    root.bonsaiJhCardStyles = true
    var style = document.createElement('style')
    style.textContent = [
      '[class*="bonsai-result-card"] {',
      '  opacity: 1;',
      '  transform: none;',
      '}',
      '.jh-card-hidden {',
      '  display: none;',
      '}',
      '[class*="bonsai-result-image-wrapper"] {',
      '  position: relative;',
      '}',
      '.jh-badge {',
      '  color: var(--color-neutral-dark-grey, #646464);',
      '  font-family: var(--font-family-general-sans, sans-serif);',
      '  font-size: var(--font-size-0, 9px);',
      '  font-weight: var(--font-weight-medium, 500);',
      '  left: var(--spacing-xs, 8px);',
      '  letter-spacing: var(--letter-spacing-1, 1px);',
      '  line-height: var(--line-height-0, 150%);',
      '  position: absolute;',
      '  text-transform: uppercase;',
      '  top: var(--spacing-xs, 8px);',
      '  z-index: var(--layer-flat, 1);',
      '}',
      '.jh-quick-view {',
      '  align-items: center;',
      '  background-color: transparent;',
      '  border: 0;',
      '  bottom: 0;',
      '  color: var(--color-brand-black, #000);',
      '  cursor: pointer;',
      '  display: flex;',
      '  font-family: var(--font-family-general-sans, sans-serif);',
      '  font-size: 22px;',
      '  font-weight: 300;',
      '  justify-content: center;',
      '  letter-spacing: 0.5px;',
      '  line-height: 1;',
      '  padding: 12px 16px;',
      '  position: absolute;',
      '  right: 0;',
      '  text-transform: uppercase;',
      '  transition: opacity var(--timing-normal, 0.3s) ease-in-out, background-color var(--timing-normal, 0.3s) ease-out, color var(--timing-normal, 0.3s) ease-out;',
      '  z-index: var(--layer-raised, 2);',
      '}',
      '[class*="bonsai-result-card"]:hover .jh-quick-view,',
      '.jh-quick-view:focus-visible {',
      '  opacity: 1;',
      '}',
      '.jh-quick-view:hover,',
      '.jh-quick-view:focus-visible {',
      '  background-color: var(--color-brand-black, #000);',
      '  color: var(--color-neutral-white, #fff);',
      '}',
      '.jh-quick-view__label {',
      '  display: none;',
      '}',
      /**
       * Wishlist King heart, matching the vendor overrides for product
       * cards; the app's own styles cannot reach into the shadow root.
       */
      'wishlist-button-collection {',
      '  margin: calc(-1 * var(--spacing-s, 12px));',
      '  position: absolute;',
      '  right: var(--spacing-xs, 8px);',
      '  top: var(--spacing-xs, 8px);',
      '  transition: opacity var(--timing-normal, 0.3s) var(--easing-normal, ease-out), visibility var(--timing-normal, 0.3s) var(--easing-normal, ease-out);',
      '  z-index: var(--layer-raised, 2);',
      '}',
      'wishlist-button-collection .wk-button {',
      '  align-items: center;',
      '  background: none;',
      '  border: 0;',
      '  cursor: pointer;',
      '  display: flex;',
      '  height: 48px;',
      '  justify-content: center;',
      '  padding: 0;',
      '  width: 48px;',
      '}',
      'wishlist-button-collection svg {',
      '  display: block;',
      '  height: 24px;',
      '  width: 24px;',
      '}',
      'wishlist-button-collection svg path {',
      '  fill: transparent;',
      '  transition: fill var(--timing-quick, 0.15s) var(--easing-normal, ease-out);',
      '}',
      'wishlist-button-collection .wk-selected svg path {',
      '  fill: var(--color-brand-black, #000);',
      '}',
      '@media (min-width: 1024px) {',
      '  wishlist-button-collection {',
      '    opacity: 0;',
      '    right: var(--spacing-m, 16px);',
      '    top: var(--spacing-m, 16px);',
      '    visibility: hidden;',
      '  }',
      '  [class*="bonsai-result-card"]:hover wishlist-button-collection,',
      '  [class*="bonsai-result-card"]:focus-within wishlist-button-collection {',
      '    opacity: 1;',
      '    visibility: visible;',
      '  }',
      '  .jh-badge {',
      '    background-color: var(--color-neutral-white, #fff);',
      '    left: var(--spacing-m, 16px);',
      '    padding: var(--spacing-2xs, 4px) var(--spacing-xs, 8px);',
      '    top: var(--spacing-m, 16px);',
      '  }',
      '  .jh-quick-view {',
      '    background-color: var(--color-neutral-white, #fff);',
      '    bottom: 10px;',
      '    font-size: 11px;',
      '    font-weight: var(--font-weight-medium, 500);',
      '    left: 14px;',
      '    opacity: 0;',
      '    padding: 9px 12px;',
      '    right: 14px;',
      '  }',
      '  .jh-quick-view__label {',
      '    display: block;',
      '  }',
      '  .jh-quick-view__plus {',
      '    display: none;',
      '  }',
      '}',
    ].join('\n')
    root.appendChild(style)
  }

  /**
   * Observe the component's results container so cards rendered by any later
   * pass (filter re-runs, load more) are also enhanced.
   * @param {Object} event - Bonsai `results` event.
   */
  function handleRender(event) {
    var element = event.target

    if (!element || !element.search || !element.search.resultsContainer) {
      return
    }

    labelClearButton(element)
    var container = element.search.resultsContainer
    var results = (event.detail && event.detail.results) || []

    if (results.length) {
      containerBadges.set(container, buildBadgeMap(results))
    }

    var query = element.search.query || ''
    var previous = followUpStates.get(container)
    followUpStates.set(container, {
      query: query,
      results: results,
      placement: previous && previous.query === query ? previous.placement : null,
    })
    containerWindows.set(container, RESULTS_WINDOW)
    enhanceCards(container)
    paintedSearches.add(element.search)
    placeLoadingState(element.search)
    showPanel(container.getRootNode().querySelector('.jh-filter-bar'), element.search)

    if (observedContainers.has(container)) {
      return
    }

    observedContainers.add(container)
    var observer = new MutationObserver(function () {
      enhanceCards(container)
    })
    observer.observe(container, { childList: true, subtree: true })
  }

  /**
   * Reuse the SDK's generated question text through its existing exported parts.
   * Options stay in the hidden native surface; the band submits only questions.
   * @param {Element} container - Current search results.
   * @returns {String[]}
   */
  function getFollowUps(container) {
    var questions = []
    container.querySelectorAll('[part~="refine-question"]').forEach(function (node) {
      var text = node.textContent.trim()
      if (text && questions.indexOf(text) === -1) {
        questions.push(text)
      }
    })
    return questions
  }

  /**
   * Category-specific product questions share the locale copy with the harness.
   * @param {String} title - Product title.
   * @returns {String[]}
   */
  function getProductQuestions(title) {
    var copy = ((window.bonsaiSearchLabels || {}).followUps || {}).product || {}
    var keys = ['similar', 'stack', 'care']
    if (/ring/i.test(title) && !/earring/i.test(title)) {
      keys[2] = 'ring'
    } else if (/bracelet/i.test(title)) {
      keys[2] = 'bracelet'
    } else if (/necklace|pendant|choker|chain/i.test(title)) {
      keys[1] = 'layer'
      keys[2] = 'chain'
    } else if (/earring|hoop/i.test(title)) {
      keys[1] = 'pair'
      keys[2] = 'everyday'
    }
    return keys.map(function (key) {
      return copy[key]
    })
  }

  /**
   * Edge button that pages the follow-up row, using the theme's chevron; it is
   * hidden while there is nothing further to scroll in its direction.
   * @param {Element} row - Follow-up row scroller.
   * @param {String} direction - `previous` or `next`.
   * @param {String} label - Accessible name.
   * @returns {Element}
   */
  function followUpScrollButton(row, direction, label) {
    var button = document.createElement('button')
    var icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    var step = direction === 'next' ? 1 : -1
    button.type = 'button'
    button.className = 'jh-follow-ups__scroll jh-follow-ups__scroll--' + direction
    button.setAttribute('aria-label', label)
    button.hidden = true
    icon.setAttribute('viewBox', '0 0 24 24')
    icon.setAttribute('aria-hidden', 'true')
    icon.innerHTML = direction === 'next'
      ? '<path d="M15.592 12.592 8.85 19.334a.505.505 0 0 1-.714-.715l6.383-6.384L8.136 5.85a.506.506 0 0 1 .714-.714l6.742 6.741a.506.506 0 0 1 0 .715Z"/>'
      : '<path d="m8.408 12.592 6.742 6.742a.505.505 0 0 0 .714-.715l-6.383-6.384 6.383-6.385a.506.506 0 0 0-.714-.714l-6.742 6.741a.506.506 0 0 0 0 .715Z"/>'
    button.appendChild(icon)
    button.addEventListener('click', function () {
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      row.scrollBy({ left: step * row.clientWidth * 0.8, behavior: reduced ? 'auto' : 'smooth' })
    })
    return button
  }

  /**
   * Supplied two-star artwork; motion is applied only by desktop hover CSS.
   * @returns {Element}
   */
  function followUpIcon() {
    var icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    icon.setAttribute('viewBox', '0 0 24 24')
    icon.setAttribute('aria-hidden', 'true')
    icon.innerHTML = '<path class="jh-star" d="M14.9181 8C14.9181 12.3939 17.115 14.5909 ' +
      '21.509 14.5909C17.115 14.5909 14.9181 16.7879 14.9181 21.1818C14.9181 16.7879 ' +
      '12.7211 14.5909 8.32715 14.5909C12.7211 14.5909 14.9181 12.3939 14.9181 8Z"/>' +
      '<path class="jh-star jh-star--small" d="M6.96351 4C6.96351 6.42424 8.17563 7.63636 ' +
      '10.5999 7.63636C8.17563 7.63636 6.96351 8.84848 6.96351 11.2727C6.96351 8.84848 ' +
      '5.75139 7.63636 3.32715 7.63636C5.75139 7.63636 6.96351 6.42424 6.96351 4Z"/>'
    return icon
  }

  /**
   * Add controls idempotently after each SDK render, including observer passes.
   * Preserve all existing grids; insert the band at the measured scroll depth.
   * - Skipped while the agent is off, as every question is answered by it.
   * @param {Element} container - The SDK's results container.
   */
  function enhanceFollowUps(container) {
    var state = followUpStates.get(container)
    var copy = (window.bonsaiSearchLabels || {}).followUps
    var bonsai = (window.cnvs && window.cnvs.bonsai) || {}
    var agentEnabled = bonsai.concierge && bonsai.concierge.enabled
    if (!state || !copy || bonsai.followUps === false || !agentEnabled) {
      return
    }
    var root = container.getRootNode()
    ensureFollowUpStyles(root)
    if (filterRoots.indexOf(root) === -1) {
      filterRoots.push(root)
    }
    container.querySelectorAll('[part~="result-card"]').forEach(function (card) {
      var wrapper = card.querySelector('[part~="result-image-wrapper"]')
      if (!wrapper || wrapper.querySelector('.jh-ask')) {
        return
      }
      var result = state.results.filter(function (item) {
        return getCardPath({ href: item.publicUrl }) === getCardPath(card)
      })[0] || {}
      var title = card.querySelector('[part~="result-title"]').textContent
      var button = document.createElement('button')
      button.type = 'button'
      button.className = 'jh-ask'
      button.setAttribute('aria-label', copy.ask.replace('{{ title }}', title))
      button.setAttribute('aria-haspopup', productExperience() === 'menu' ? 'menu' : 'dialog')
      button.setAttribute('aria-expanded', 'false')
      button.appendChild(followUpIcon())
      var animateStar = function (event) {
        var desktopHover = '(min-width: 1024px) and (hover: hover) and (pointer: fine)'
        if (event.pointerType === 'touch' || !window.matchMedia(desktopHover).matches ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          return
        }
        button.querySelectorAll('.jh-star').forEach(function (star, index) {
          star.getAnimations().forEach(function (animation) {
            animation.cancel()
          })
          star.animate([
            { opacity: 0, transform: 'scale(0.2) rotate(-70deg)' },
            { opacity: 1, transform: 'scale(1) rotate(0)' },
          ], {
            duration: 550,
            delay: index * 120,
            easing: 'cubic-bezier(0.34, 1.4, 0.64, 1)',
            iterations: 1,
            fill: 'backwards',
          })
        })
      }
      var prefetch = function () {
        if (window.bonsaiPrefetchProductQuestions) {
          window.bonsaiPrefetchProductQuestions(getCardPath(card).split('/').pop())
        }
      }
      card.addEventListener('pointerenter', animateStar)
      button.addEventListener('pointerenter', animateStar)
      card.addEventListener('pointerenter', prefetch, { once: true })
      button.addEventListener('focus', prefetch, { once: true })
      button.addEventListener('click', function (event) {
        event.preventDefault()
        event.stopPropagation()
        if (productExperience() === 'menu') {
          openProductMenu(root, card, button, result, title)
        } else {
          openProductDrawer(root, card, button, result, title)
        }
      })
      wrapper.appendChild(button)
    })
    var questions = getFollowUps(container)
    if (!questions.length) {
      return
    }
    container.querySelectorAll('[part~="refine"]').forEach(function (element) {
      element.classList.add('jh-native-follow-ups')
    })
    var band = container.querySelector('.jh-follow-ups')
    if (band && band.dataset.questions !== JSON.stringify(questions)) {
      band.remove()
      band = null
    }
    if (band || state.placementTimer) {
      return
    }
    state.placementTimer = setTimeout(function () {
      state.placementTimer = null
      if (followUpStates.get(container) === state) {
        insertFollowUpBand(container, state, copy)
      }
    }, 200)
  }

  /**
   * Add one question band near 60% of the rendered search scroll area.
   * Existing recommendation and More results grids and controls stay intact.
   * @param {Element} container - Results for one mounted search surface.
   * @param {Object} state - Current query and results.
   * @param {Object} copy - Localized follow-up labels.
   */
  function insertFollowUpBand(container, state, copy) {
    if (container.querySelector('.jh-follow-ups')) {
      return
    }
    var questions = getFollowUps(container)
    if (!questions.length) {
      return
    }
    var allCards = container.querySelectorAll('[part~="result-card"]')
    var cards = Array.from(allCards).filter(function (card) {
      return card.getBoundingClientRect().height > 0
    })
    if (!cards.length) {
      return
    }
    var band = document.createElement('nav')
    band.className = 'jh-follow-ups'
    band.setAttribute('aria-label', copy.label)
    band.dataset.questions = JSON.stringify(questions)
    band.dataset.questionSource = 'generated'
    var row = document.createElement('div')
    row.className = 'jh-follow-ups__row'
    band.appendChild(row)
    questions.forEach(function (text, index) {
      var button = document.createElement('button')
      button.type = 'button'
      button.className = 'jh-follow-ups__chip'
      button.appendChild(followUpIcon())
      var span = document.createElement('span')
      span.textContent = text
      button.appendChild(span)
      button.addEventListener('click', function () {
        button.dispatchEvent(new CustomEvent('bonsai:follow-up', {
          bubbles: true, composed: true,
          detail: {
            text: text, source: 'search_follow_up', query: state.query, position: index + 1,
          },
        }))
      })
      row.appendChild(button)
    })
    var previous = followUpScrollButton(row, 'previous',
      copy.scrollPrevious || FALLBACK_LABELS.scrollPrevious)
    var next = followUpScrollButton(row, 'next', copy.scrollNext || FALLBACK_LABELS.scrollNext)
    var updateScrollButtons = function () {
      previous.hidden = row.scrollLeft <= 1
      next.hidden = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1
    }
    band.appendChild(previous)
    band.appendChild(next)
    row.addEventListener('scroll', updateScrollButtons, { passive: true })
    new ResizeObserver(updateScrollButtons).observe(row)
    var last = cards[cards.length - 1]
    last.insertAdjacentElement('afterend', band)
    var root = container.getRootNode()
    var host = root.host && root.host.closest('.bonsai-search-drawer')
    var overlay = host && host.closest('.search-drawer-overlay')
    var scroller = overlay && overlay.querySelector('.search-drawer-overlay__container')
    scroller = scroller || document.scrollingElement
    var start = scroller === document.scrollingElement
      ? -window.scrollY
      : scroller.getBoundingClientRect().top - scroller.scrollTop
    var target = start + scroller.scrollHeight * 0.6
    var rows = cards.filter(function (card, index) {
      var next = cards[index + 1]
      return !next || next.parentElement !== card.parentElement ||
        Math.abs(next.getBoundingClientRect().top - card.getBoundingClientRect().top) > 1
    })
    var nearest = rows.reduce(function (best, card) {
      var distance = Math.abs(card.getBoundingClientRect().bottom - target)
      var bestDistance = Math.abs(best.getBoundingClientRect().bottom - target)
      return distance < bestDistance ? card : best
    }, rows[0])
    nearest.insertAdjacentElement('afterend', band)
    var measureBand = function () {
      if (!band.isConnected) {
        observer.disconnect()
        return
      }
      var rect = band.getBoundingClientRect()
      var left = scroller === document.scrollingElement
        ? 0
        : scroller.getBoundingClientRect().left
      band.style.setProperty('--jh-band-left', left - rect.left + 'px')
      band.style.setProperty('--jh-band-width', scroller.clientWidth + 'px')
    }
    var observer = new ResizeObserver(measureBand)
    observer.observe(scroller)
    observer.observe(band)
    measureBand()
  }

  /**
   * Product questions reuse the filter drawer's close and keyboard handling.
   * @param {ShadowRoot} root - Search shadow root.
   * @param {Element} card - Result anchor.
   * @param {Element} trigger - Sparkle to restore on close.
   * @param {Object} result - Search result for product grounding.
   * @param {String} title - Clean product title.
   */
  function openProductDrawer(root, card, trigger, result, title) {
    var old = root.querySelector('.jh-product-drawer-wrapper')
    if (old) {
      old.closeDrawer()
      old.remove()
    }
    var body = document.createElement('div')
    var controller = new AbortController()
    var scrollTarget = root.host.closest('.search-drawer-overlay') || document.body
    var overflow = scrollTarget.style.overflow
    var wrapper = buildTrigger('', body, title, {
      product: true,
      onToggle: function (open) {
        trigger.setAttribute('aria-expanded', String(open))
        scrollTarget.style.overflow = open ? 'hidden' : overflow
        document.documentElement.classList.toggle('jh-product-questions-open', open)
        if (!open) {
          controller.abort()
          clearDrawerPath(trigger, false)
          trigger.focus({ preventScroll: true })
        }
      },
    })
    wrapper.className = 'jh-product-drawer-wrapper'
    var proxy = wrapper.querySelector('.jh-trigger')
    proxy.hidden = true
    var drawer = wrapper.querySelector('.jh-drawer')
    var header = wrapper.querySelector('.jh-drawer__header')
    var thumb = document.createElement('img')
    thumb.src = card.querySelector('img').src
    thumb.alt = ''
    thumb.width = 40
    thumb.height = 40
    header.insertBefore(thumb, header.firstChild)
    populateProductQuestions(root, card, trigger, result, title, wrapper, body, controller)
    drawer.addEventListener('touchmove', function (event) {
      if (event.target === drawer) {
        event.preventDefault()
      }
    }, { passive: false })
    root.appendChild(wrapper)
    proxy.click()
  }

  /** Reuse identical curated/fallback questions and product dispatch in both views. */
  function populateProductQuestions(
    root, card, trigger, result, title, wrapper, body, controller, onReady,
  ) {
    var handle = getCardPath(card).split('/').pop()
    var name = body.getAttribute('aria-label')
    var renderQuestions = function (questions) {
      if (controller.signal.aborted || !wrapper.isConnected) {
        return
      }
      body.removeAttribute('aria-busy')
      if (name) {
        body.setAttribute('aria-label', name)
      } else {
        body.removeAttribute('aria-label')
      }
      body.textContent = ''
      questions.forEach(function (text, index) {
        var button = document.createElement('button')
        button.type = 'button'
        button.className = 'jh-product-question'
        button.appendChild(followUpIcon())
        var span = document.createElement('span')
        span.textContent = text
        button.appendChild(span)
        button.addEventListener('click', function () {
          wrapper.closeDrawer()
          trigger.dispatchEvent(new CustomEvent('bonsai:product-follow-up', {
            bubbles: true, composed: true,
            detail: {
              text: text, handle: getCardPath(card).split('/').pop(), bonsaiId: result.id,
              title: title, source: 'product_follow_up', position: index + 1,
              query: root.host.search.query,
            },
          }))
        })
        body.appendChild(button)
      })
      if (onReady) {
        onReady()
      }
    }
    body.setAttribute('aria-busy', 'true')
    body.setAttribute('aria-label', window.bonsaiSearchLabels.followUps.loading)
    for (var index = 0; index < 4; index++) {
      var skeleton = document.createElement('div')
      skeleton.className = 'jh-product-question-skeleton'
      skeleton.setAttribute('aria-hidden', 'true')
      body.appendChild(skeleton)
    }
    var settled = false
    var complete = function (questions) {
      if (settled) {
        return
      }
      settled = true
      clearTimeout(deadline)
      body.dataset.questionSource = questions.length ? 'curated' : 'fallback'
      renderQuestions(questions.length ? questions : getProductQuestions(title))
    }
    var deadline = setTimeout(function () {
      complete([])
      controller.abort()
    }, 5000)
    controller.signal.addEventListener('abort', function () {
      clearTimeout(deadline)
    }, { once: true })
    if (window.bonsaiProductQuestions) {
      window.bonsaiProductQuestions(handle, controller.signal).then(complete, function () {
        complete([])
      })
    } else {
      setTimeout(function () {
        complete([])
      })
    }
  }

  /** Desktop opens product questions as a menu; smaller screens use the drawer. */
  function productExperience() {
    return window.matchMedia('(min-width: 1024px)').matches ? 'menu' : 'panel'
  }

  /** Anchored, nonmodal alternative; the existing panel remains intact. */
  function openProductMenu(root, card, trigger, result, title) {
    var old = root.querySelector('.jh-product-drawer-wrapper')
    var wasOpen = trigger.getAttribute('aria-expanded') === 'true'
    if (old) {
      old.closeDrawer()
      old.remove()
    }
    if (wasOpen) {
      return
    }
    var wrapper = document.createElement('div')
    wrapper.className = 'jh-product-drawer-wrapper'
    var menu = document.createElement('div')
    menu.className = 'jh-product-menu is-open'
    menu.setAttribute('popover', 'manual')
    menu.setAttribute('role', 'menu')
    menu.setAttribute('aria-label', title)
    menu.tabIndex = -1
    wrapper.appendChild(menu)
    root.appendChild(wrapper)
    var controller = new AbortController()
    var listeners = new AbortController()
    var close = function (restoreFocus) {
      controller.abort()
      listeners.abort()
      trigger.setAttribute('aria-expanded', 'false')
      wrapper.remove()
      if (restoreFocus && trigger.isConnected) {
        trigger.focus({ preventScroll: true })
      }
    }
    wrapper.closeDrawer = function () {
      close(true)
    }
    trigger.setAttribute('aria-expanded', 'true')
    menu.showPopover()
    var position = function () {
      var anchor = trigger.getBoundingClientRect()
      var bounds = menu.getBoundingClientRect()
      var width = document.documentElement.clientWidth
      var height = window.innerHeight
      var left = anchor.right + 8
      var top = anchor.top
      if (width < 640) {
        left = anchor.right - bounds.width
        top = anchor.bottom + 8
      } else if (left + bounds.width > width - 12) {
        left = anchor.left - bounds.width - 8
      }
      menu.style.left = Math.max(12, Math.min(left, width - bounds.width - 12)) + 'px'
      menu.style.top = Math.max(12, Math.min(top, height - bounds.height - 12)) + 'px'
    }
    var ready = function () {
      menu.querySelectorAll('button').forEach(function (button) {
        button.setAttribute('role', 'menuitem')
        button.tabIndex = -1
      })
      position()
      if (root.activeElement === menu) {
        menu.querySelector('button').focus({ preventScroll: true })
      }
    }
    populateProductQuestions(root, card, trigger, result, title, wrapper, menu, controller, ready)
    position()
    menu.focus({ preventScroll: true })
    var outside = function (event) {
      if (!menu.contains(event.target) && !trigger.contains(event.target)) {
        close(false)
      }
    }
    root.addEventListener('pointerdown', outside, { signal: listeners.signal })
    document.addEventListener('pointerdown', function (event) {
      if (event.composedPath().indexOf(root.host) === -1) {
        close(false)
      }
    }, { capture: true, signal: listeners.signal })
    menu.addEventListener('keydown', function (event) {
      var items = Array.from(menu.querySelectorAll('button'))
      var index = items.indexOf(root.activeElement)
      if (event.key === 'Tab') {
        close(true)
        return
      }
      var next = { ArrowDown: (index + 1) % items.length,
        ArrowUp: (index - 1 + items.length) % items.length,
        Home: 0, End: items.length - 1 }[event.key]
      if (next !== undefined && items[next]) {
        event.preventDefault()
        event.stopPropagation()
        items[next].focus()
      }
    }, { signal: listeners.signal })
    window.addEventListener('scroll', function (event) {
      if (!menu.contains(event.target)) {
        close(false)
      }
    }, { capture: true, signal: listeners.signal })
    window.addEventListener('resize', function () {
      close(false)
    }, { signal: listeners.signal })
  }

  /**
   * Review treatments and directional drawer overrides, isolated to search.
   * @param {ShadowRoot} root - Search shadow root.
   */
  function ensureFollowUpStyles(root) {
    if (root.querySelector('#jh-follow-up-styles')) {
      return
    }
    var style = document.createElement('style')
    style.id = 'jh-follow-up-styles'
    style.textContent = FOLLOW_UP_STYLES
    root.appendChild(buildStyles())
    root.appendChild(style)
  }

  // Match the full-width band with white sticky backdrops; keep dropdowns unclipped.
  var FOLLOW_UP_STYLES = [
    '[part~="search-bar"]::before,',
    '[part~="filter-panel"]::before {',
    '  box-shadow: 0 0 0 100vmax var(--color-neutral-white, #fff);',
    '  clip-path: inset(0 -100vmax);',
    '  content: "";',
    '  inset: 0;',
    '  pointer-events: none;',
    '  position: absolute;',
    '  z-index: -1;',
    '}',
    '',
    '[part~="search-bar"]::before {',
    '  clip-path: inset(calc(-1 * var(--spacing-m, 16px)) -100vmax 0);',
    '  inset: -1px;',
    '}',
    '@media (width < 1024px) {',
    '  [part~="filter-panel"]::before {',
    '    bottom: calc(-1 * var(--spacing-m, 16px));',
    '    top: calc(-1 * var(--spacing-s, 12px));',
    '  }',
    '}',
    '.jh-native-follow-ups { display: none !important; }',
    '.jh-product-question-skeleton {',
    '  background: var(--color-skeleton-background, #f0f0f0);',
    '  height: 28px;',
    '  margin-block: 8px;',
    '}',
    '',
    '.jh-follow-ups {',
    '  --jh-band-bg: #ececec;',
    '  --jh-row-gutter: var(--spacing-m, 16px);',
    '  isolation: isolate;',
    '  position: relative;',
    '  box-sizing: border-box;',
    '  font-family: var(--font-family-general-sans, "General Sans", sans-serif);',
    '  grid-column: 1 / -1;',
    '  margin-inline: -16px;',
    '  min-width: 0;',
    '  padding: var(--spacing-xl, 32px) 0 var(--spacing-xl, 32px) var(--jh-row-gutter);',
    '}',
    '',
    /**
     * One row at every width, scrolling sideways once the pills overflow. It
     * runs to the band's right edge so a cut-off pill shows there is more; the
     * end padding still stops the last pill at the gutter.
     */
    '.jh-follow-ups__row {',
    '  display: flex;',
    '  gap: var(--spacing-xs, 8px);',
    '  margin: -4px 0 -4px -4px;',
    '  overflow-x: auto;',
    '  overscroll-behavior-x: contain;',
    '  padding: 4px var(--jh-row-gutter) 4px 4px;',
    '  scrollbar-width: none;',
    '}',
    '.jh-follow-ups__scroll {',
    '  align-items: center;',
    '  border: 0;',
    '  bottom: var(--spacing-xl, 32px);',
    '  color: var(--color-neutral-black, #000);',
    '  cursor: pointer;',
    '  display: flex;',
    '  padding: 0;',
    '  position: absolute;',
    '  top: var(--spacing-xl, 32px);',
    '  width: var(--spacing-5xl, 64px);',
    '  z-index: 1;',
    '}',
    '.jh-follow-ups__scroll[hidden] { display: none; }',
    '.jh-follow-ups__scroll svg {',
    '  fill: currentColor;',
    '  flex: 0 0 auto;',
    '  height: var(--icon-s, 20px);',
    '  width: var(--icon-s, 20px);',
    '}',
    /** A long, eased fade so the pill dissolves into the band under the chevron. */
    '.jh-follow-ups__scroll--previous {',
    '  background: linear-gradient(to right, var(--jh-band-bg) 15%, ' +
      'color-mix(in srgb, var(--jh-band-bg) 85%, transparent) 35%, ' +
      'color-mix(in srgb, var(--jh-band-bg) 55%, transparent) 55%, ' +
      'color-mix(in srgb, var(--jh-band-bg) 25%, transparent) 75%, transparent);',
    '  justify-content: flex-start;',
    '  left: 0;',
    '  padding-inline-start: var(--jh-row-gutter);',
    '  width: calc(var(--jh-row-gutter) + var(--spacing-5xl, 64px));',
    '}',
    '.jh-follow-ups__scroll--next {',
    '  background: linear-gradient(to left, var(--jh-band-bg) 15%, ' +
      'color-mix(in srgb, var(--jh-band-bg) 85%, transparent) 35%, ' +
      'color-mix(in srgb, var(--jh-band-bg) 55%, transparent) 55%, ' +
      'color-mix(in srgb, var(--jh-band-bg) 25%, transparent) 75%, transparent);',
    '  justify-content: flex-end;',
    '  padding-inline-end: var(--spacing-xs, 8px);',
    '  right: 0;',
    '}',
    '.jh-follow-ups__scroll:focus-visible {',
    '  outline: 2px solid #f15a24;',
    '  outline-offset: -2px;',
    '}',
    '.jh-follow-ups__row::-webkit-scrollbar { display: none; }',
    '.jh-follow-ups__row .jh-follow-ups__chip { flex: 0 0 auto; }',
    '@media (width < 1024px) {',
    '  .jh-follow-ups__row .jh-follow-ups__chip {',
    '    max-width: min(280px, calc(100vw - 64px));',
    '  }',
    '}',
    '.jh-follow-ups::before {',
    '  background: var(--jh-band-bg);',
    '  bottom: 0;',
    '  box-sizing: border-box;',
    '  content: "";',
    '  left: var(--jh-band-left, 0);',
    '  pointer-events: none;',
    '  position: absolute;',
    '  top: 0;',
    '  width: var(--jh-band-width, 100%);',
    '  z-index: -1;',
    '}',
    '.jh-product-menu {',
    '  background: #fff;',
    '  border: 1px solid #d6d6d6;',
    '  box-shadow: 0 6px 24px rgb(0 0 0 / 12%);',
    '  box-sizing: border-box;',
    '  color: #33383c;',
    '  font-family: var(--font-family-general-sans, "General Sans", sans-serif);',
    '  inset: auto;',
    '  margin: 0;',
    '  max-height: calc(100dvh - 24px);',
    '  overflow: auto;',
    '  padding: 8px;',
    '  position: fixed;',
    '  width: min(280px, calc(100vw - 24px));',
    '}',
    '.jh-product-menu .jh-product-question { color: #33383c; padding: 10px 8px; width: 100%; }',
    '[part~="result-card"] wishlist-button-collection {',
    '  display: none;',
    '}',
    '',
    '.jh-follow-ups__chip {',
    '  align-items: center;',
    '  background: #000;',
    '  border: 0;',
    '  border-radius: 0;',
    '  color: #fff;',
    '  cursor: pointer;',
    '  display: flex;',
    '  font: inherit;',
    '  font-size: 13px;',
    '  gap: 6px;',
    '  min-height: 44px;',
    '  padding: var(--spacing-xs, 8px) var(--spacing-m, 16px);',
    '  text-align: left;',
    '}',
    '',
    '.jh-follow-ups__chip:active {',
    '  background: var(--color-neutral-charcoal, #33383c);',
    '}',
    '',
    '.jh-follow-ups__chip:focus-visible,',
    '.jh-ask:focus-visible,',
    '.jh-product-question:focus-visible {',
    '  outline: 2px solid #f15a24;',
    '  outline-offset: 2px;',
    '}',
    '',
    '.jh-follow-ups svg,',
    '.jh-product-question svg {',
    '  flex: 0 0 18px;',
    '  height: 18px;',
    '  width: 18px;',
    '}',
    '',
    '.jh-ask {',
    '  background: transparent;',
    '  border: 0;',
    '  top: 0;',
    '  color: #000;',
    '  cursor: pointer;',
    '  display: grid;',
    '  height: 44px;',
    '  padding: 12px;',
    '  place-items: center;',
    '  position: absolute;',
    '  right: 0;',
    '  width: 44px;',
    '  z-index: var(--layer-raised, 2);',
    '}',
    '',
    '.jh-ask svg {',
    '  height: 24px;',
    '  width: 24px;',
    '}',
    '@media (width >= 1024px) and (hover: hover) and (pointer: fine) {',
    '  .jh-ask:hover,',
    '  .jh-ask[aria-expanded="true"] {',
    '    color: var(--color-brand-orange, #ff5c1d);',
    '  }',
    '}',
    '.jh-star {',
    '  fill: currentColor;',
    '  stroke: currentColor;',
    '  stroke-width: 1.13636;',
    '  stroke-linejoin: round;',
    '  transform-box: fill-box;',
    '  transform-origin: center;',
    '}',
    '@keyframes jh-star-bloom {',
    '  from { opacity: 0; transform: scale(0.2) rotate(-70deg); }',
    '  to { opacity: 1; transform: scale(1) rotate(0); }',
    '}',
    '@media (width >= 1024px) and (hover: hover) and (pointer: fine) {',
    '  .jh-follow-ups__chip:hover .jh-star,',
    '  .jh-product-question:hover .jh-star {',
    '    animation: jh-star-bloom 550ms cubic-bezier(0.34, 1.4, 0.64, 1) both;',
    '  }',
    '  .jh-follow-ups__chip:hover .jh-star--small,',
    '  .jh-product-question:hover .jh-star--small {',
    '    animation-delay: 120ms;',
    '  }',
    '  .jh-product-menu .jh-product-question:hover .jh-star {',
    '    animation: none;',
    '  }',
    '  .jh-product-menu .jh-product-question svg:hover .jh-star {',
    '    animation: jh-star-bloom 550ms cubic-bezier(0.34, 1.4, 0.64, 1) both;',
    '  }',
    '}',
    '',
    '.jh-drawer--product {',
    '  background: rgb(0 0 0 / 35%);',
    '  font-family: var(--font-family-general-sans, "General Sans", sans-serif);',
    '}',
    '',
    '.jh-drawer--product .jh-drawer__panel {',
    '  bottom: 0;',
    '  height: auto;',
    '  max-height: 70dvh;',
    '  max-width: none;',
    '  position: absolute;',
    '  translate: 0 100%;',
    '  width: 100%;',
    '}',
    '',
    '.jh-drawer--product.is-open .jh-drawer__panel {',
    '  translate: 0 0;',
    '}',
    '',
    '.jh-drawer--product .jh-drawer__header {',
    '  border-bottom: 1px solid #d6d6d6;',
    '  gap: 12px;',
    '  padding: 12px 16px;',
    '}',
    '',
    '.jh-drawer--product .jh-drawer__header img {',
    '  background: #f5f5f5;',
    '  object-fit: cover;',
    '}',
    '',
    '.jh-drawer--product .jh-drawer__title {',
    '  color: #757575;',
    '  font-size: 13px;',
    '  margin: 0;',
    '  order: 1;',
    '  text-align: left;',
    '}',
    '',
    '.jh-drawer--product .jh-drawer__close {',
    '  flex: 0 0 44px;',
    '  height: 44px;',
    '  order: 2;',
    '  width: 44px;',
    '}',
    '',
    '.jh-drawer--product .jh-drawer__body {',
    '  gap: 0;',
    '  overscroll-behavior: contain;',
    '  padding: 8px 16px 16px;',
    '  padding-bottom: max(16px, env(safe-area-inset-bottom));',
    '}',
    '',
    '.jh-product-question {',
    '  align-items: center;',
    '  background: #fff;',
    '  border: 0;',
    '  border-radius: 0;',
    '  color: #000;',
    '  cursor: pointer;',
    '  display: flex;',
    '  font: inherit;',
    '  font-size: 13px;',
    '  gap: 12px;',
    '  min-height: 44px;',
    '  padding: 8px 0;',
    '  text-align: left;',
    '}',
    '',
    '.jh-product-question:hover {',
    '  background: #f0f0f0;',
    '}',
    '@media (width < 1024px) {',
    '  .jh-drawer--product .jh-drawer__header {',
    '    padding: 16px;',
    '  }',
    '',
    '  .jh-drawer--product .jh-drawer__header img {',
    '    flex: 0 0 40px;',
    '    height: 40px;',
    '    max-width: 40px;',
    '    width: 40px;',
    '  }',
    '',
    '  .jh-drawer--product .jh-drawer__title {',
    '    -webkit-box-orient: vertical;',
    '    color: var(--color-neutral-charcoal, #33383c);',
    '    display: -webkit-box;',
    '    font-size: 16px;',
    '    -webkit-line-clamp: 2;',
    '    line-height: 22px;',
    '    min-width: 0;',
    '    overflow: hidden;',
    '    overflow-wrap: anywhere;',
    '    white-space: normal;',
    '  }',
    '',
    '  .jh-drawer--product .jh-drawer__body {',
    '    gap: 8px;',
    '    padding: 12px 8px max(16px, env(safe-area-inset-bottom));',
    '  }',
    '',
    '  .jh-drawer--product .jh-product-question {',
    '    align-items: flex-start;',
    '    box-sizing: border-box;',
    '    color: var(--color-neutral-charcoal, #33383c);',
    '    line-height: 20px;',
    '    min-height: 44px;',
    '    padding: 10px 8px;',
    '    white-space: normal;',
    '    width: 100%;',
    '  }',
    '',
    '  .jh-drawer--product .jh-product-question span {',
    '    min-width: 0;',
    '    overflow-wrap: anywhere;',
    '  }',
    '',
    '  .jh-drawer--product .jh-product-question svg {',
    '    margin-top: 1px;',
    '  }',
    '}',
    '@media (width >= 1024px) {',
    '  .jh-follow-ups {',
    '    --jh-row-gutter: var(--spacing-l, 24px);',
    '    margin-inline: -24px;',
    '  }',
    '',
    '  .jh-follow-ups__chip {',
    '    font-size: 14px;',
    '  }',
    '',
    '  .jh-follow-ups__row {',
    '    gap: var(--spacing-m, 16px);',
    '  }',
    '}',
    '@media (prefers-reduced-motion: reduce) {',
    '  .jh-follow-ups__chip:hover .jh-star,',
    '  .jh-product-question:hover .jh-star,',
    '  .jh-product-menu .jh-product-question svg:hover .jh-star {',
    '    animation: none;',
    '  }',
    '',
    '  .jh-drawer--product,',
    '  .jh-drawer--product .jh-drawer__panel {',
    '    transition: none;',
    '  }',
    '}',
  ].join('\n')

  document.addEventListener('results', handleRender)

  /** Close stale product dialogs before a query replaces their trigger cards. */
  document.addEventListener('search', function (event) {
    var search = event.target && event.target.search
    if (!search || !search.resultsContainer) {
      return
    }
    var state = followUpStates.get(search.resultsContainer)
    if (state) {
      clearTimeout(state.placementTimer)
    }
    followUpStates.delete(search.resultsContainer)
    var band = search.resultsContainer.querySelector('.jh-follow-ups')
    if (band) {
      band.remove()
    }
    var root = search.resultsContainer.getRootNode()
    var wrapper = root.querySelector('.jh-product-drawer-wrapper')
    if (wrapper) {
      if (wrapper.querySelector('.is-open')) {
        wrapper.closeDrawer()
      }
      wrapper.remove()
    }
  })

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) {
      return
    }
    filterRoots.forEach(function (root) {
      var wrapper = root.querySelector('.jh-product-drawer-wrapper')
      if (wrapper && wrapper.querySelector('.is-open')) {
        wrapper.closeDrawer()
      }
    })
  })

  /**
   * Reserve the suggestions dropdown's height on the drawer so the top
   * searches and banners sit below the list rather than behind it, which is
   * what makes the list read as part of the page instead of a dropdown; the
   * `is-suggesting` state also stands the drawer's own suggestions down while
   * the real list is open.
   * - The SDK renders the dropdown absolutely inside the search bar and hides
   *   it whenever a pointerdown lands outside that bar, so it cannot be moved
   *   into the flow without making the suggestions unclickable.
   * - The class observer catches the dropdown opening and closing, the size
   *   observer the overlay hiding and re-showing the drawer, which the class
   *   alone would miss as the dropdown keeps its class while hidden.
   * @param {Element} element - The <bonsai-search> element.
   */
  function trackSuggestions(element) {
    var dropdown = element.search && element.search.suggestionsDropdown
    var drawer = element.closest('.bonsai-search-drawer')

    if (!dropdown || !drawer) {
      return
    }

    var update = function () {
      var visible = dropdown.classList.contains('bonsai-visible')
      drawer.style.setProperty('--suggestions-height', visible ? dropdown.offsetHeight + 'px' : '')
      drawer.classList.toggle('is-suggesting', visible)
    }

    if (!observedDropdowns.has(dropdown)) {
      observedDropdowns.add(dropdown)

      var observer = new MutationObserver(update)
      observer.observe(dropdown, {
        attributeFilter: ['class'],
        attributes: true,
        childList: true,
        subtree: true,
      })

      new ResizeObserver(update).observe(dropdown)
    }

    update()
  }

  /**
   * Replace the clear button's icon with the theme's label, matching the
   * search drawer's own clear control.
   * @param {Element} element - The <bonsai-search> element.
   */
  function labelClearButton(element) {
    var button = element.search && element.search.clearBtn
    var text = label('clear')

    if (!button || button.textContent === text) {
      return
    }

    button.textContent = text
  }

  /**
   * Enhance the search bar the first time it is focused, the earliest point
   * the suggestions and clear button can show.
   * - `focusin` is composed, so it arrives here retargeted to the host.
   * @param {Object} event - Focus event from the search input.
   */
  function handleFocus(event) {
    var element = event.target

    if (!element || element.tagName !== 'BONSAI-SEARCH') {
      return
    }

    trackSuggestions(element)
    labelClearButton(element)
    watchSuggestionsFocus(element)
  }

  document.addEventListener('focusin', handleFocus)

  /**
   * Hide the suggestions once focus moves beyond the search bar — to the
   * filters, the results, or out of the component — as the SDK only closes
   * the list on an outside pointerdown, which leaves it hanging for
   * keyboard shoppers tabbing on.
   * - Listens inside the shadow tree, where `relatedTarget` is the real
   *   next element; from the document every move within the component
   *   retargets to the host and reads as internal.
   * @param {Element} element - The <bonsai-search> element.
   */
  function watchSuggestionsFocus(element) {
    var bar = element.search && element.search.searchBar

    if (!bar || bar.bonsaiJhFocusHide) {
      return
    }

    bar.bonsaiJhFocusHide = true

    bar.addEventListener('focusout', function (event) {
      if (event.relatedTarget && bar.contains(event.relatedTarget)) {
        return
      }

      var dropdown = element.search.suggestionsDropdown

      if (dropdown) {
        dropdown.classList.remove('bonsai-visible')
      }
    })
  }

  /**
   * Filter panel renderer matching the collection top bar's "Filter by:"
   * dropdown row. Renders into the component's closed shadow root, so styles
   * are carried inline; theme CSS custom properties still inherit through.
   * @param {Object} context - Bonsai filter panel render context.
   * @returns {Element}
   */
  window.bonsaiSearchFilterPanel = function (context) {
    var panel = document.createElement('div')
    panel.className = 'jh-filter-bar'
    panel.appendChild(buildStyles())

    var dimensions = context.dimensions.filter(function (dimension) {
      return dimension.type === 'keyword' || isPriceDimension(dimension)
    })

    var inline = document.createElement('div')
    inline.className = 'jh-filter-bar__inline'
    panel.appendChild(inline)

    var groups = buildGroups(context, dimensions)

    if (groups.length) {
      var heading = document.createElement('span')
      heading.className = 'jh-filter-bar__label'
      heading.textContent = label('filter') + ':'
      inline.appendChild(heading)

      groups.forEach(function (group) {
        inline.appendChild(group)
      })
    }

    inline.appendChild(buildSort(context))

    var pills = buildPills(context)

    if (pills) {
      panel.appendChild(pills)
    }

    panel.appendChild(buildTriggers(context, dimensions))

    restoreTriggerFocus(panel)
    attachRootClose(panel)
    hideEmptyPanel(panel, context)
    return panel
  }

  /**
   * Build a pill for each selected filter value, which removes it when
   * clicked, matching the collection page's active filters.
   * - Sits between the two layouts: below the dropdowns on desktop, above the
   *   filter and sort buttons on mobile.
   * @param {Object} context - Bonsai filter panel render context.
   * @returns {Element|null} - Null when nothing is selected.
   */
  function buildPills(context) {
    var filters = context.filters || {}
    var row = document.createElement('div')
    row.className = 'jh-pills'

    Object.keys(filters).forEach(function (field) {
      var values = Array.isArray(filters[field]) ? filters[field] : []

      values.forEach(function (value) {
        row.appendChild(buildPill(context, String(value), function () {
          toggleFilterValue(context, field, value)
        }))
      })
    })

    if (filters[PRICE_FIELD] && !Array.isArray(filters[PRICE_FIELD])) {
      var text = priceLabel(priceRangeFromFilter(filters[PRICE_FIELD]))

      row.appendChild(buildPill(context, text, function () {
        setPriceFilter(context, null)
      }))
    }

    return row.children.length ? row : null
  }

  /**
   * Build one selected filter's pill.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {String} value - Text shown on the pill.
   * @param {Function} onRemove - Removes the filter the pill stands for.
   * @returns {Element}
   */
  function buildPill(context, value, onRemove) {
    var pill = document.createElement('button')
    pill.type = 'button'
    pill.className = 'jh-pill'
    pill.setAttribute('aria-label', label('removeFilter').replace('{{ name }}', value))

    var text = document.createElement('span')
    text.textContent = value

    var remove = document.createElement('span')
    remove.className = 'jh-pill__remove'
    remove.textContent = '\u2715'

    pill.appendChild(text)
    pill.appendChild(remove)

    pill.addEventListener('click', onRemove)

    return pill
  }

  /**
   * Build a dropdown for each filter dimension that has options to show.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Array} dimensions - Keyword and price filter dimensions.
   * @returns {Array}
   */
  function buildGroups(context, dimensions, options) {
    var groups = []

    dimensions.forEach(function (dimension) {
      var group = isPriceDimension(dimension)
        ? buildPriceGroup(context, dimension, options)
        : buildGroup(context, dimension, options)

      if (group) {
        groups.push(group)
      }
    })

    return groups
  }

  /**
   * Build the mobile filter and sort row, where each button opens the choices
   * in a drawer rather than a dropdown, matching the collection top bar.
   * - The dropdowns are built a second time for the filter drawer as an
   *   element can only sit in one of the two layouts.
   * - Both drawers hold their choices until applied: running a search rebuilds
   *   this row, which would close the drawer the shopper is still using.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Array} dimensions - Keyword filter dimensions.
   * @returns {Element}
   */
  function buildTriggers(context, dimensions) {
    var row = document.createElement('div')
    row.className = 'jh-filter-bar__triggers'

    var pending = { changed: false, filters: context.sdk.getFilters() || {} }

    var groups = buildGroups(context, dimensions, {
      onChange: function (field, value) {
        pending.changed = true
        toggleValue(pending.filters, field, value)
      },
      onPriceChange: function (range) {
        pending.changed = true
        setPrice(pending.filters, range)
      },
    })

    if (groups.length) {
      var filters = document.createElement('div')
      filters.className = 'jh-drawer__filters'

      groups.forEach(function (group) {
        filters.appendChild(group)
      })

      row.appendChild(buildTrigger(label('filter'), filters, label('filterTitle'), {
        onApply: function () {
          if (!pending.changed) {
            return false
          }

          context.sdk.setFilters(pending.filters, { run: true })
          return true
        },
      }))
    }

    var options = buildSortOptions(context, { apply: false, name: 'jh-sort-drawer' })

    row.appendChild(buildTrigger(label('sort'), options.element, label('sortTitle'), {
      onApply: options.apply,
    }))

    return row
  }

  /**
   * Build one trigger button and the drawer it opens.
   * @param {String} label - Button label.
   * @param {Element} body - Drawer contents.
   * @param {String} title - Drawer title.
   * @param {Object} [options] - `onApply` runs before the drawer closes.
   * @returns {Element}
   */
  function buildTrigger(text, body, title, options) {
    var settings = options || {}

    var trigger = document.createElement('button')
    trigger.type = 'button'
    trigger.className = 'jh-trigger'
    trigger.textContent = text

    var drawer = document.createElement('div')
    drawer.className = 'jh-drawer' + (settings.product ? ' jh-drawer--product' : '')
    drawer.setAttribute('role', 'dialog')
    drawer.setAttribute('aria-modal', 'true')
    drawer.setAttribute('aria-label', title)

    var panel = document.createElement('div')
    panel.className = 'jh-drawer__panel'

    var header = document.createElement('div')
    header.className = 'jh-drawer__header'

    var close = document.createElement('button')
    close.type = 'button'
    close.className = 'jh-drawer__close'
    close.setAttribute('aria-label', label('close'))
    close.textContent = '\u2715'

    var heading = document.createElement('span')
    heading.className = 'jh-drawer__title'
    heading.textContent = title

    var apply = document.createElement('button')
    apply.type = 'button'
    apply.className = 'jh-drawer__apply'
    apply.textContent = label('apply')

    /**
     * Focus returns to the trigger on close, as the theme's focus handling
     * cannot see the drawer inside the closed shadow root and would
     * otherwise leave focus at the start of the component.
     * - The path is restored once the drawer has faded out rather than as it
     *   starts to, as the bar would otherwise rise over it on its way out;
     *   reopening before then leaves the path cleared.
     */
    var setOpen = function (open) {
      drawer.classList.toggle('is-open', open)
      if (settings.onToggle) {
        settings.onToggle(open)
      }
      trigger.setAttribute('aria-expanded', String(open))

      if (open) {
        clearDrawerPath(drawer, true)
        close.focus()
      } else {
        trigger.focus()
      }
    }

    drawer.addEventListener('transitionend', function (event) {
      var hidden = event.target === drawer && event.propertyName === 'visibility'

      if (hidden && !drawer.classList.contains('is-open')) {
        clearDrawerPath(drawer, false)
      }
    })

    trigger.addEventListener('click', function (event) {
      event.preventDefault()
      event.stopPropagation()
      setOpen(true)
    })

    close.addEventListener('click', function () {
      setOpen(false)
    })

    drawer.addEventListener('click', function (event) {
      if (event.target === drawer) {
        setOpen(false)
      }
    })

    /**
     * Applying re-runs the search, which rebuilds the panel and replaces the
     * trigger focused on close, so the rebuilt one is remembered to focus.
     */
    apply.addEventListener('click', function () {
      setOpen(false)

      if (settings.onApply && settings.onApply()) {
        pendingTriggerFocus = text
      }
    })

    body.classList.add('jh-drawer__body')
    header.appendChild(close)
    header.appendChild(heading)
    panel.appendChild(header)
    panel.appendChild(body)
    if (!settings.product) {
      panel.appendChild(apply)
    }
    drawer.appendChild(panel)

    var wrapper = document.createElement('div')
    wrapper.appendChild(trigger)
    wrapper.appendChild(drawer)
    wrapper.closeDrawer = function () {
      setOpen(false)
    }
    return wrapper
  }

  /**
   * Sort options, matching the values the search API accepts.
   * - Labels come from the theme's locales via the drawer.
   */
  var SORT_OPTIONS = ['relevance', 'price_asc', 'price_desc']

  /**
   * Build the sort control at the end of the filter row, matching the
   * collection top bar's sort dropdown.
   * - The SDK renders its own control above the results rather than in the
   *   filter row, so it stays off and the theme sets the sort the same way
   *   that control does.
   * @param {Object} context - Bonsai filter panel render context.
   * @returns {Element}
   */
  function buildSort(context) {
    var current = currentSort(context)

    var wrapper = document.createElement('div')
    wrapper.className = 'jh-filter-bar__sort'

    var heading = document.createElement('span')
    heading.className = 'jh-filter-bar__label'
    heading.textContent = label('sort') + ':'

    var group = document.createElement('div')
    group.className = 'jh-filter jh-filter--sort'

    var toggle = document.createElement('button')
    toggle.type = 'button'
    toggle.className = 'jh-filter__toggle'
    toggle.setAttribute('aria-expanded', 'false')
    toggle.textContent = sortLabel(current)

    var caret = document.createElement('span')
    caret.className = 'jh-filter__caret'
    toggle.appendChild(caret)

    var dropdown = buildSortOptions(context, { apply: true, name: 'jh-sort' }).element
    dropdown.classList.add('jh-filter__dropdown')

    toggle.addEventListener('click', function (event) {
      event.stopPropagation()
      var isOpen = group.classList.toggle('is-open')
      toggle.setAttribute('aria-expanded', String(isOpen))
      closeGroups(group.closest('.jh-filter-bar'), group)
    })

    group.appendChild(toggle)
    group.appendChild(dropdown)
    wrapper.appendChild(heading)
    wrapper.appendChild(group)
    return wrapper
  }

  /**
   * The order the results are currently in.
   * @param {Object} context - Bonsai filter panel render context.
   * @returns {String}
   */
  function currentSort(context) {
    return (context.sdk && context.sdk.currentSort) || 'relevance'
  }

  /**
   * Build the list of sort options.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Object} options - `apply` sorts on choosing rather than on the
   *                           drawer's apply button, `name` groups the radios.
   * @returns {Object} - The list, and an apply function for the drawer.
   */
  function buildSortOptions(context, options) {
    var pending = currentSort(context)

    var list = document.createElement('div')
    list.className = 'jh-sort-options'

    SORT_OPTIONS.forEach(function (value) {
      var option = document.createElement('label')
      option.className = 'jh-filter__option'

      var input = document.createElement('input')
      input.type = 'radio'
      input.name = options.name
      input.checked = value === pending
      input.addEventListener('change', function () {
        pending = value

        if (options.apply) {
          applySort(context.sdk, value)
        }
      })

      var text = document.createElement('span')
      text.textContent = sortLabel(value)

      option.appendChild(input)
      option.appendChild(text)
      list.appendChild(option)
    })

    return {
      apply: function () {
        return applySort(context.sdk, pending)
      },
      element: list,
    }
  }

  /**
   * Re-run the search with a new sort order.
   * - Mirrors the SDK's own sort control: the order is held on the instance
   *   and applied by re-running the current query and filters.
   * @param {Object} sdk - The component's inner search instance.
   * @param {String} value - Sort option value.
   */
  function applySort(sdk, value) {
    if (!sdk || sdk.currentSort === value) {
      return false
    }

    sdk.currentSort = value
    sdk.runWithCurrentFilters(sdk.inBrowseMode())
    return true
  }

  /**
   * Hide the whole panel until a search has been made and its results are in,
   * and when no dropdowns rendered, matching collection filters where
   * zero-count options are not shown.
   * - The SDK keeps the dimensions it has already fetched when the search is
   *   reset, so without the submitted check the row would come back before
   *   the next search rather than only after it.
   * - The row arrives with the first cards and then stays, so that filtering
   *   and sorting refine the results in place rather than appearing to start
   *   again; it only goes away when the search is reset.
   * @param {Element} panel - The filter bar element.
   * @param {Object} context - Bonsai filter panel render context.
   */
  function hideEmptyPanel(panel, context) {
    setTimeout(function () {
      placeLoadingState(context.sdk)
      showPanel(panel, context.sdk)
      clearDrawerPath(panel, false)
    })
  }

  /**
   * Clear the search bar out of a drawer's way while it covers the page.
   * - The drawer renders inside the filter row, so it cannot rise above the
   *   sticky bar on its own; the row's own z-index caps everything in it.
   * - The row is lifted by the drawer's `has-open-drawer` state rather than
   *   an inline style, as the theme's `::part()` rule outranks styles set
   *   inside the shadow root.
   * - The search overlay the drawer renders in is lifted above the site
   *   header, which shares its layer while search is open; left to page
   *   order, iOS Safari draws the header over the drawer's top.
   * @param {Element} node - Any element in the component's shadow tree.
   * @param {Boolean} open - Whether a drawer is open.
   */
  function clearDrawerPath(node, open) {
    var root = node.getRootNode()
    var host = root.host && root.host.closest('.bonsai-search-drawer')

    if (!host) {
      return
    }

    var overlay = host.closest('.search-drawer-overlay')
    host.classList.toggle('has-open-drawer', open)

    if (overlay) {
      overlay.style.zIndex = open ? 'var(--layer-temporary, 20)' : ''
    }
  }

  /**
   * Show or hide one filter row for the state its search is in.
   * @param {Element} panel - The filter bar element.
   * @param {Object} sdk - The component's inner search instance.
   */
  function showPanel(panel, sdk) {
    var host = panel && panel.parentElement

    if (!host || !sdk) {
      return
    }

    var painted = paintedSearches.has(sdk)
    var ready = sdk.hasSubmittedSearch && (painted || sdk.state !== 'loading')
    host.style.display = ready && panel.querySelector('.jh-filter') ? '' : 'none'
  }

  /**
   * Move the loading state below the filter row, so the row holds its place
   * and the results swap in underneath it.
   * - The SDK renders the loading state above the row; it is only ever
   *   addressed by reference, so it can be moved without breaking it.
   * @param {Object} sdk - The component's inner search instance.
   */
  function placeLoadingState(sdk) {
    var panel = sdk && sdk.filterPanel
    var loading = sdk && sdk.loadingState

    if (!panel || !loading || loading.previousElementSibling === panel) {
      return
    }

    panel.after(loading)

    if (sdk.loadingThumbs) {
      loading.after(sdk.loadingThumbs)
    }
  }

  /**
   * Build one filter dropdown.
   * - Options with a zero count are hidden to match collection filters,
   *   unless currently selected.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Object} dimension - Filter dimension with field and values.
   * @param {Object} [options] - `onChange` takes over from filtering directly.
   * @returns {Element|null}
   */
  function buildGroup(context, dimension, options) {
    var settings = options || {}
    var selected = context.filters && context.filters[dimension.field]
    var selectedValues = Array.isArray(selected) ? selected : []

    var values = (dimension.values || []).filter(function (raw) {
      var value = raw && typeof raw === 'object' ? raw.value : raw
      var count = raw && typeof raw === 'object' ? raw.count : null

      return count !== 0 || selectedValues.indexOf(value) > -1
    })

    if (!values.length) {
      return null
    }

    return buildChoiceGroup(dimension, values.map(function (raw) {
      var value = raw && typeof raw === 'object' ? raw.value : raw

      return {
        checked: selectedValues.indexOf(value) > -1,
        label: String(value),
        onChange: function () {
          if (settings.onChange) {
            settings.onChange(dimension.field, value)
            return
          }

          toggleFilterValue(context, dimension.field, value)
        },
      }
    }))
  }

  /**
   * Build the price dropdown from the fixed ranges.
   * - Price is a single range, so choosing one replaces any other.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Object} dimension - Price filter dimension.
   * @param {Object} [options] - `onPriceChange` takes over from filtering
   *                             directly.
   * @returns {Element}
   */
  function buildPriceGroup(context, dimension, options) {
    var settings = options || {}
    var selected = context.filters && context.filters[PRICE_FIELD]

    return buildChoiceGroup(dimension, PRICE_RANGES.map(function (range) {
      return {
        checked: isSelectedPrice(selected, range),
        label: priceLabel(range),
        onChange: function (input) {
          var next = input.checked ? range : null

          if (settings.onPriceChange) {
            settings.onPriceChange(next)
            return
          }

          setPriceFilter(context, next)
        },
      }
    }), true)
  }

  /**
   * Build a dropdown of checkbox choices.
   * @param {Object} dimension - Filter dimension, for the toggle's label.
   * @param {Array} choices - `checked`, `label` and `onChange` for each.
   * @param {Boolean} [single] - Only one choice can be checked at a time.
   * @returns {Element}
   */
  function buildChoiceGroup(dimension, choices, single) {
    var group = document.createElement('div')
    group.className = 'jh-filter'

    var toggle = document.createElement('button')
    toggle.type = 'button'
    toggle.className = 'jh-filter__toggle'
    toggle.setAttribute('aria-expanded', 'false')
    toggle.textContent = groupLabel(dimension)

    var caret = document.createElement('span')
    caret.className = 'jh-filter__caret'
    toggle.appendChild(caret)

    var dropdown = document.createElement('div')
    dropdown.className = 'jh-filter__dropdown'

    choices.forEach(function (choice) {
      var option = document.createElement('label')
      option.className = 'jh-filter__option'

      var input = document.createElement('input')
      input.type = 'checkbox'
      input.checked = choice.checked
      input.addEventListener('change', function () {
        if (single && input.checked) {
          dropdown.querySelectorAll('input').forEach(function (other) {
            other.checked = other === input
          })
        }

        choice.onChange(input)
      })

      var text = document.createElement('span')
      text.textContent = choice.label

      option.appendChild(input)
      option.appendChild(text)
      dropdown.appendChild(option)
    })

    toggle.addEventListener('click', function (event) {
      event.stopPropagation()
      var isOpen = group.classList.toggle('is-open')
      toggle.setAttribute('aria-expanded', String(isOpen))
      closeGroups(group.closest('.jh-filter-bar'), group)
    })

    group.appendChild(toggle)
    group.appendChild(dropdown)
    return group
  }

  /**
   * Whether a dimension is the price range.
   * @param {Object} dimension - Filter dimension.
   * @returns {Boolean}
   */
  function isPriceDimension(dimension) {
    return dimension.field === PRICE_FIELD && dimension.type === 'integer_range'
  }

  /**
   * Convert a range in dollars to the API's filter in cents.
   * @param {Object} range - Range with an optional `min` and `max`.
   * @returns {Object}
   */
  function priceFilter(range) {
    var filter = {}

    if (range.min !== undefined) {
      filter.min = range.min * 100
    }

    if (range.max !== undefined) {
      filter.max = range.max * 100 - 1
    }

    return filter
  }

  /**
   * Convert an active price filter back to a range in dollars.
   * @param {Object} filter - Price filter in cents.
   * @returns {Object}
   */
  function priceRangeFromFilter(filter) {
    var range = {}

    if (filter.min !== undefined) {
      range.min = Math.round(filter.min / 100)
    }

    if (filter.max !== undefined) {
      range.max = Math.round((filter.max + 1) / 100)
    }

    return range
  }

  /**
   * Whether the active price filter is a range.
   * @param {Object} [selected] - Active price filter in cents.
   * @param {Object} range - Range in dollars.
   * @returns {Boolean}
   */
  function isSelectedPrice(selected, range) {
    var filter = priceFilter(range)

    return !!selected && selected.min === filter.min && selected.max === filter.max
  }

  /**
   * Format whole dollars like the theme's prices.
   * @param {Number} dollars - Amount in whole dollars.
   * @returns {String}
   */
  function formatDollars(dollars) {
    var formatted = window.bonsaiSearchFormatPrice && window.bonsaiSearchFormatPrice(dollars)

    return formatted || '$' + dollars.toLocaleString('en-US')
  }

  /**
   * Read one price label template, falling back to English.
   * - A missing translation comes through as a "Translation missing" string
   *   rather than an empty one, so it is treated as absent.
   * @param {String} name - Template name: `under`, `range` or `over`.
   * @returns {String}
   */
  function priceTemplate(name) {
    var labels = (window.bonsaiSearchLabels || {}).priceRanges || {}
    var template = labels[name]

    if (typeof template !== 'string' || template.indexOf('Translation missing') === 0) {
      return FALLBACK_LABELS.priceRanges[name]
    }

    return template
  }

  /**
   * Read a price range's label, e.g. "Under $500" or "$5,000+".
   * @param {Object} range - Range in dollars.
   * @returns {String}
   */
  function priceLabel(range) {
    if (range.min === undefined) {
      return priceTemplate('under').replace('{{ price }}', formatDollars(range.max))
    }

    if (range.max === undefined) {
      return priceTemplate('over').replace('{{ price }}', formatDollars(range.min))
    }

    return priceTemplate('range')
      .replace('{{ min }}', formatDollars(range.min))
      .replace('{{ max }}', formatDollars(range.max))
  }

  /**
   * Set or clear the price range in a set of filters.
   * @param {Object} filters - Filters to change.
   * @param {Object|null} range - Range in dollars, or null to clear.
   */
  function setPrice(filters, range) {
    if (range) {
      filters[PRICE_FIELD] = priceFilter(range)
      return
    }

    delete filters[PRICE_FIELD]
  }

  /**
   * Set the price range and re-run the search.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Object|null} range - Range in dollars, or null to clear.
   */
  function setPriceFilter(context, range) {
    var filters = context.sdk.getFilters() || {}
    setPrice(filters, range)
    context.sdk.setFilters(filters, { run: true })
  }

  /**
   * Toggle a filter value and re-run the search.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {String} field - Filter dimension field.
   * @param {String|Boolean} value - Value to toggle.
   */
  function toggleFilterValue(context, field, value) {
    var filters = context.sdk.getFilters() || {}
    toggleValue(filters, field, value)
    context.sdk.setFilters(filters, { run: true })
  }

  /**
   * Add or remove one value from a set of filters.
   * @param {Object} filters - Filters to change.
   * @param {String} field - Filter dimension field.
   * @param {String} value - Filter value.
   */
  function toggleValue(filters, field, value) {
    var current = Array.isArray(filters[field]) ? filters[field].slice() : []
    var index = current.indexOf(value)

    if (index > -1) {
      current.splice(index, 1)
    } else {
      current.push(value)
    }

    if (current.length) {
      filters[field] = current
      return
    }

    delete filters[field]
  }

  /**
   * Close open dropdowns, optionally keeping one.
   * @param {Element} panel - The filter bar element.
   * @param {Element} [keep] - Group to leave open.
   */
  function closeGroups(panel, keep) {
    if (!panel) {
      return
    }

    panel.querySelectorAll('.jh-filter.is-open').forEach(function (group) {
      if (group === keep) {
        return
      }

      group.classList.remove('is-open')
      group.querySelector('.jh-filter__toggle').setAttribute('aria-expanded', 'false')
    })
  }

  /**
   * Focus the rebuilt trigger once the re-rendered panel is attached, so a
   * keyboard shopper's apply lands them back on the button they came from.
   * - Held through the settle window rather than set once, as the panel is
   *   built before the SDK shows it, and the theme's focus trap re-focuses
   *   its own first element on a timer after the results update, which
   *   would take the focus straight back.
   * - Stands down at the shopper's next press or tap so it never fights
   *   real input, and after a second and a half in any case.
   * @param {Element} panel - The freshly built filter panel.
   */
  function restoreTriggerFocus(panel) {
    if (!pendingTriggerFocus) {
      return
    }

    var text = pendingTriggerFocus
    var attempts = 0
    var cancelled = false

    var cancel = function () {
      cancelled = true
    }

    window.addEventListener('keydown', cancel, { capture: true, once: true })
    window.addEventListener('pointerdown', cancel, { capture: true, once: true })

    var cleanup = function () {
      window.removeEventListener('keydown', cancel, true)
      window.removeEventListener('pointerdown', cancel, true)
    }

    var attempt = function () {
      if (cancelled || pendingTriggerFocus !== text) {
        cleanup()
        return
      }

      attempts += 1

      if (attempts > 30) {
        pendingTriggerFocus = null
        cleanup()
        return
      }

      var triggers = panel.querySelectorAll('.jh-trigger')

      for (var index = 0; index < triggers.length; index += 1) {
        var trigger = triggers[index]

        if (trigger.textContent === text && trigger.offsetParent !== null) {
          if (panel.getRootNode().activeElement !== trigger) {
            trigger.focus()
          }

          break
        }
      }

      setTimeout(attempt, 50)
    }

    setTimeout(attempt)
  }

  /**
   * Close dropdowns on any click outside them, listening on the shadow root
   * so retargeting doesn't hide the real click target, and register the root
   * with the Escape handling below.
   * @param {Element} panel - The filter bar element.
   */
  function attachRootClose(panel) {
    setTimeout(function () {
      var root = panel.getRootNode()

      if (!root || root.bonsaiJhFilterClose) {
        return
      }

      root.bonsaiJhFilterClose = true
      filterRoots.push(root)
      root.addEventListener('click', function (event) {
        var target = event.target
        var inFilter = target.closest && target.closest('.jh-filter')
        closeGroups(root.querySelector('.jh-filter-bar'), inFilter)
      })
    })
  }

  /**
   * Close an open filter drawer or dropdown on Escape and keep the key from
   * the theme, so the search overlay itself only closes once nothing in the
   * filter bar is left open.
   * - Captured on the window as focus can sit outside the closed shadow root,
   *   where the theme's own window listener would otherwise act first.
   * @param {Object} event - Keydown event.
   */
  function handleEscape(event) {
    if (event.key !== 'Escape') {
      return
    }

    filterRoots.forEach(function (root) {
      var menu = root.querySelector('.jh-product-menu.is-open')
      if (menu) {
        menu.parentElement.closeDrawer()
        event.stopPropagation()
        event.preventDefault()
        return
      }
      var drawer = root.querySelector('.jh-drawer.is-open')
      var group = root.querySelector('.jh-filter.is-open')

      if (drawer) {
        drawer.querySelector('.jh-drawer__close').click()
      } else if (group) {
        closeGroups(root.querySelector('.jh-filter-bar'))
      } else {
        return
      }

      event.stopPropagation()
    })
  }

  window.addEventListener('keydown', handleEscape, true)

  /**
   * Trap Tab inside an open filter or sort drawer, as the theme's focus trap
   * cannot see into the closed shadow root and the browser would otherwise
   * walk the results and the page behind the overlay.
   * - The theme's own handler still runs; it stands down while focus sits in
   *   a web component, so only this trap moves focus.
   * @param {Object} event - Keydown event.
   */
  function handleTrapTab(event) {
    if (event.key !== 'Tab') {
      return
    }

    filterRoots.forEach(function (root) {
      var drawer = root.querySelector('.jh-drawer.is-open')

      if (!drawer) {
        return
      }

      var focusables = []

      drawer.querySelectorAll('button, input, select, textarea, a[href]').forEach(function (element) {
        if (!element.disabled && element.offsetParent !== null) {
          focusables.push(element)
        }
      })

      if (!focusables.length) {
        return
      }

      var index = focusables.indexOf(root.activeElement)
      var next = index + (event.shiftKey ? -1 : 1)

      if (index === -1) {
        next = 0
      } else if (next < 0) {
        next = focusables.length - 1
      } else if (next >= focusables.length) {
        next = 0
      }

      event.preventDefault()
      focusables[next].focus()
    })
  }

  window.addEventListener('keydown', handleTrapTab, true)

  /**
   * Human label for a filter dimension, e.g. "stone_type" > "Stone Type".
   * @param {Object} dimension - Filter dimension.
   * @returns {String}
   */
  function groupLabel(dimension) {
    if (dimension.label) {
      return dimension.label
    }

    return dimension.field
      .replace(/^tag:/i, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\b[a-z]/g, function (letter) {
        return letter.toUpperCase()
      })
  }

  /**
   * Styles for the filter bar, scoped inside the component's shadow root.
   * @returns {Element}
   */
  function buildStyles() {
    var style = document.createElement('style')
    style.textContent = [
      '.jh-filter-bar, .jh-filter-bar * {',
      '  box-sizing: border-box;',
      '}',
      '.jh-filter-bar {',
      '  font-family: var(--font-family-general-sans, sans-serif);',
      '  padding-block: 0 8px;',
      '}',
      '.jh-filter-bar__inline {',
      '  align-items: baseline;',
      '  display: none;',
      '  flex-wrap: wrap;',
      '  gap: 8px 32px;',
      '}',
      '.jh-filter-bar__label {',
      '  color: var(--color-neutral-dark-grey, #646464);',
      '  font-size: 13px;',
      '  letter-spacing: 0.5px;',
      '}',
      '.jh-filter-bar__sort {',
      '  align-items: baseline;',
      '  display: flex;',
      '  gap: 12px;',
      '  margin-inline-start: auto;',
      '}',
      '.jh-filter {',
      '  position: relative;',
      '}',
      '.jh-filter__toggle {',
      '  align-items: center;',
      '  background: none;',
      '  border: 0;',
      '  color: var(--color-neutral-charcoal, #33383c);',
      '  cursor: pointer;',
      '  display: flex;',
      '  font-family: inherit;',
      '  font-size: 13px;',
      '  gap: 10px;',
      '  letter-spacing: 0.5px;',
      '  padding: 0;',
      '}',
      '.jh-filter__caret {',
      '  border-block-end: 1px solid currentcolor;',
      '  border-inline-end: 1px solid currentcolor;',
      '  height: 5px;',
      '  margin-block-start: -3px;',
      '  transform: rotate(45deg);',
      '  transition: transform var(--timing-quick, 0.15s) ease-out;',
      '  width: 5px;',
      '}',
      '.jh-filter.is-open .jh-filter__caret {',
      '  margin-block-start: 3px;',
      '  transform: rotate(225deg);',
      '}',
      '.jh-filter__dropdown {',
      '  background-color: var(--color-neutral-white, #fff);',
      '  box-shadow: 0 8px 15px 0 rgb(0 0 0 / 8%);',
      '  display: none;',
      '  left: 0;',
      '  max-height: 320px;',
      '  min-width: 250px;',
      '  overflow-y: auto;',
      '  padding: 20px;',
      '  position: absolute;',
      '  top: calc(100% + 12px);',
      '  z-index: var(--layer-heightened, 4);',
      '}',
      '.jh-filter--sort .jh-filter__dropdown {',
      '  left: auto;',
      '  right: 0;',
      '}',
      '.jh-filter.is-open .jh-filter__dropdown {',
      '  display: flex;',
      '  flex-direction: column;',
      '  gap: 14px;',
      '}',
      '.jh-filter__option {',
      '  align-items: center;',
      '  color: var(--color-neutral-charcoal, #33383c);',
      '  cursor: pointer;',
      '  display: flex;',
      '  font-size: 13px;',
      '  gap: 12px;',
      '  letter-spacing: 0.5px;',
      '  white-space: nowrap;',
      '}',
      '.jh-pills {',
      '  display: flex;',
      '  flex-wrap: wrap;',
      '  gap: 8px;',
      '  padding-block: 0 12px;',
      '}',
      '.jh-pill {',
      '  align-items: center;',
      '  background-color: var(--color-neutral-black, #000);',
      '  border: 0;',
      '  color: var(--color-neutral-white, #fff);',
      '  cursor: pointer;',
      '  display: flex;',
      '  font-family: inherit;',
      '  font-size: 13px;',
      '  gap: 12px;',
      '  letter-spacing: 0.5px;',
      '  padding: 7px 10px;',
      '}',
      '.jh-pill__remove {',
      '  font-size: 11px;',
      '  line-height: 1;',
      '}',
      /**
       * Mobile: the choices move into drawers behind a pair of buttons,
       * matching the collection top bar.
       */
      '.jh-filter-bar__triggers {',
      '  display: grid;',
      '  gap: 8px;',
      '  grid-auto-columns: 1fr;',
      '  grid-auto-flow: column;',
      '}',
      '.jh-trigger {',
      '  background: none;',
      '  border: 1px solid var(--color-neutral-light-grey, #e6e6e6);',
      '  color: var(--color-neutral-dark-grey, #646464);',
      '  cursor: pointer;',
      '  font-family: inherit;',
      '  font-size: 11px;',
      '  font-weight: var(--font-weight-medium, 500);',
      '  letter-spacing: 1px;',
      '  padding: 14px 8px;',
      '  text-transform: uppercase;',
      '  width: 100%;',
      '}',
      /**
       * The scrim fades and the panel slides in from the left, matching the
       * theme's drawer overlay transitions; visibility keeps the closed
       * drawer out of the tab order while still letting the close animation
       * play.
       */
      '.jh-drawer {',
      '  background-color: var(--color-overlay, rgb(0 0 0 / 30%));',
      '  inset: 0;',
      '  opacity: 0;',
      '  position: fixed;',
      '  transition: opacity var(--timing-normal, 0.3s) var(--easing-normal, ease-out), visibility var(--timing-normal, 0.3s) var(--easing-normal, ease-out);',
      '  visibility: hidden;',
      '  z-index: var(--layer-window-overlay, 12);',
      '}',
      '.jh-drawer.is-open {',
      '  opacity: 1;',
      '  visibility: visible;',
      '}',
      '.jh-drawer__panel {',
      '  background-color: var(--color-neutral-white, #fff);',
      '  display: flex;',
      '  flex-direction: column;',
      '  height: 100%;',
      '  max-width: 360px;',
      '  transition: translate var(--timing-normal, 0.3s) var(--easing-normal, ease-out);',
      '  translate: -100% 0;',
      '  width: 86%;',
      '}',
      '.jh-drawer.is-open .jh-drawer__panel {',
      '  translate: 0;',
      '}',
      '.jh-drawer__header {',
      '  align-items: center;',
      '  display: flex;',
      '  padding: 24px 16px;',
      '}',
      '.jh-drawer__close {',
      '  background: none;',
      '  border: 0;',
      '  color: var(--color-neutral-black, #000);',
      '  cursor: pointer;',
      '  font-family: inherit;',
      '  font-size: 20px;',
      '  line-height: 1;',
      '  padding: 0;',
      '}',
      '.jh-drawer__title {',
      '  color: var(--color-neutral-black, #000);',
      '  flex: 1;',
      '  font-size: 15px;',
      '  margin-inline-end: 20px;',
      '  text-align: center;',
      '}',
      '.jh-drawer__body {',
      '  display: flex;',
      '  flex: 1;',
      '  flex-direction: column;',
      '  gap: 20px;',
      '  overflow-y: auto;',
      '  padding: 8px 16px 24px;',
      '}',
      '.jh-drawer__apply {',
      '  background-color: var(--color-neutral-black, #000);',
      '  border: 0;',
      '  color: var(--color-neutral-white, #fff);',
      '  cursor: pointer;',
      '  font-family: inherit;',
      '  font-size: 11px;',
      '  font-weight: var(--font-weight-medium, 500);',
      '  letter-spacing: 1px;',
      '  margin: 16px;',
      '  padding: 16px;',
      '  text-transform: uppercase;',
      '}',
      /**
       * Filter dropdowns become accordions once inside the drawer.
       */
      '.jh-drawer__filters .jh-filter__dropdown {',
      '  box-shadow: none;',
      '  max-height: none;',
      '  min-width: 0;',
      '  padding: 12px 0 0;',
      '  position: static;',
      '}',
      '.jh-filter__option input {',
      '  accent-color: var(--color-neutral-black, #000);',
      '  flex: 0 0 16px;',
      '  height: 16px;',
      '  margin: 0;',
      '  width: 16px;',
      '}',
      /**
       * Desktop: the dropdown row shows and the trigger buttons stand down.
       */
      '@media (min-width: 1024px) {',
      '  .jh-filter-bar {',
      '    padding-block: 16px;',
      '  }',
      '  .jh-filter-bar__inline {',
      '    display: flex;',
      '  }',
      '  .jh-filter-bar__triggers {',
      '    display: none;',
      '  }',
      '  .jh-pills {',
      '    padding-block: 12px 0;',
      '  }',
      '}',
    ].join('\n')
    return style
  }
})()
